import React, { useEffect, useState } from "react";
import { BarChart3, ChevronDown, Flame, Snowflake } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n";

const POS_COLOR = { A: "#b91c1c", B: "#1d4ed8", C: "#047857", D: "#6d28d9" };

/* 30-day digit frequency per position (A/B/C) — hot digits glow, cold digits fade */
export function WinPatternBoard({ gameId, onPick }) {
  const { lang } = useI18n();
  const ta = lang === "ta";
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get(`/games/${gameId}/digit-stats`, { params: { days: 30 } }).then((r) => setD(r.data)).catch(() => {}); }, [gameId]);
  if (!d || !d.draws) return null;
  return (
    <div className="rl-card overflow-hidden" data-testid="win-pattern-board">
      <button onClick={() => setOpen((v) => !v)} className="w-full px-4 py-3 flex items-center justify-between" data-testid="win-pattern-toggle">
        <span className="font-display font-bold text-sm text-slate-900 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-amber-500" />{ta ? "வெற்றி எண் பேட்டர்ன் · 30 நாள்" : "Win Pattern Board · 30 days"} <span className="text-[10px] text-slate-400 font-normal">{d.draws} {ta ? "டிரா" : "draws"}</span></span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {!open && (
        <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid="win-pattern-hot-summary">
          {d.positions.map((p) => <span key={p} className="text-[11px] font-bold px-2 py-1 rounded-full bg-orange-50 text-orange-800 border border-orange-200"><Flame className="w-3 h-3 inline -mt-0.5" /> {p}: {d.hot[p].join(" ")}</span>)}
        </div>
      )}
      {open && (
        <div className="px-4 pb-4 space-y-3">
          {d.positions.map((p) => {
            const max = Math.max(1, ...d.counts[p]);
            return (
              <div key={p} data-testid={`win-pattern-pos-${p}`}>
                <div className="flex items-center justify-between text-[11px] mb-1">
                  <span className="font-bold" style={{ color: POS_COLOR[p] }}>{ta ? "போர்டு" : "Board"} {p}</span>
                  <span className="text-slate-500"><Flame className="w-3 h-3 inline text-orange-500" /> {ta ? "ஹாட்" : "hot"} {d.hot[p].join(", ")} · <Snowflake className="w-3 h-3 inline text-sky-500" /> {ta ? "கோல்ட்" : "cold"} {d.cold[p].join(", ")}</span>
                </div>
                <div className="grid grid-cols-10 gap-1 items-end h-16">
                  {d.counts[p].map((c, digit) => {
                    const hot = d.hot[p].includes(digit), cold = d.cold[p].includes(digit);
                    return (
                      <button key={digit} onClick={() => onPick?.(p, digit)} className="flex flex-col items-center justify-end h-full" title={`${digit}: ${c}× · last seen ${d.last_seen[p]?.[digit] ?? "—"} draws ago`} data-testid={`win-pattern-${p}-${digit}`}>
                        <span className="text-[9px] font-bold text-slate-500 leading-none mb-0.5">{c}</span>
                        <span className="w-full rounded-t-md" style={{ height: `${Math.max(6, (c / max) * 40)}px`, background: hot ? POS_COLOR[p] : cold ? "#cbd5e1" : `${POS_COLOR[p]}77` }} />
                        <span className={`text-[11px] font-mono font-black mt-0.5 ${hot ? "text-slate-900" : "text-slate-500"}`}>{digit}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="text-[10px] text-slate-500">{ta ? "கடைசி 10 ரிசல்ட்" : "Last 10 results"}: <span className="font-mono font-bold text-slate-700" data-testid="win-pattern-recent">{d.recent.join(" · ")}</span></div>
          <div className="text-[10px] text-slate-400">{ta ? "பேட்டர்ன் கடந்த கால ரிசல்ட் மட்டும் — வெற்றி உத்தரவாதம் இல்லை." : "Patterns show past draws only — no result is guaranteed."}</div>
        </div>
      )}
    </div>
  );
}
